import { skipToken } from '@reduxjs/toolkit/query/react'
import { useMemo } from 'react'

import { useOpenWindow, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  InventoryScreen,
  ONEC_PRICE_TAG_WINDOWS,
  cameraScanAvailable,
  inventoryClearRequested,
  inventoryCountChanged,
  inventoryCreateRequested,
  inventoryOperationDismissed,
  inventoryOperationResendRequested,
  inventoryReloadRequested,
  inventoryReprintSet,
  inventoryRowRemoved,
  inventorySaveRequested,
  inventoryScanRequested,
  inventoryWarehouseChosen,
  makeSelectInventoryVm,
  normalizeRid,
  onecInventorySession,
  onecStructure,
  priceTagsPicked,
  selectChosenWarehouse,
  selectStructureWarehouses,
  selectInventoryReprints,
  type InventoryActions,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'

/**
 * Binding окна «Инвентаризация 1С». rid — из окна, склад — выбор внутри этой
 * базы (D-064). Сессия читается с сервера при каждом открытии окна: обход
 * продолжается после F5 и с другого рабочего места.
 */
export function InventoryBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = normalizeRid(window.params.rid ?? '')
  const intent = useAppSelector((state) => state.onec.inventory)
  const choice = useAppSelector((state) => state.onec.inventoryWarehouse)
  const chosen = selectChosenWarehouse(choice, resourceId)
  const warehouseRefKey = chosen?.refKey ?? ''

  const resourceArgs = useMemo(() => (resourceId === '' ? null : { resourceId }), [resourceId])
  const warehouses = useQueryResult(onecStructure, resourceArgs)

  const sessionArgs = useMemo(
    () => (resourceId === '' || warehouseRefKey === '' ? skipToken : { resourceId, warehouseRefKey }),
    [resourceId, warehouseRefKey],
  )
  // Сессия — на сервере: при открытии окна перечитывается, чтобы не продолжать чужой устаревший снимок.
  onecInventorySession.useQuerySubscription(sessionArgs, { refetchOnMountOrArgChange: true })
  const selectSession = useMemo(() => onecInventorySession.select(sessionArgs), [sessionArgs])
  const sessionResult = useAppSelector(selectSession)
  const session = sessionArgs === skipToken ? null : sessionResult

  const selectVm = useMemo(() => makeSelectInventoryVm(), [])
  const vm = selectVm({ resourceId, choice, intent, warehouses, session, cameraScanAvailable: cameraScanAvailable() })

  const actions = useMemo<InventoryActions>(() => {
    const target = { resourceId, warehouseRefKey }
    return {
      warehouseChosen: (refKey) => {
        const found = (warehouses.data === undefined ? [] : selectStructureWarehouses(warehouses.data.data)).find((item) => item.refKey === refKey)
        if (found === undefined) return
        dispatch(inventoryWarehouseChosen({ resourceId, warehouse: { refKey: found.refKey, name: found.name } }))
      },
      reloadWarehouses: () => dispatch(inventoryReloadRequested({ resourceId, warehouseRefKey: null })),
      reloadSession: () => dispatch(inventoryReloadRequested(target)),
      scanSubmitted: (barcode) => dispatch(inventoryScanRequested({ ...target, barcode })),
      countChanged: (rowId, counted) => dispatch(inventoryCountChanged({ ...target, rowId, counted })),
      reprintToggled: (rowId) => {
        const row = vm.rows.find((item) => item.id === rowId)
        if (row !== undefined) dispatch(inventoryReprintSet({ ...target, rowId, reprint: !row.reprint }))
      },
      rowRemoved: (rowId) => dispatch(inventoryRowRemoved({ ...target, rowId })),
      saveNow: () => dispatch(inventorySaveRequested(target)),
      clearSession: () => dispatch(inventoryClearRequested(target)),
      createProduct: (request) => {
        const row = vm.rows.find((item) => item.id === request.rowId)
        if (row === undefined) return
        dispatch(inventoryCreateRequested({ ...target, ...request, barcode: row.barcode }))
      },
      operationResent: (key) => dispatch(inventoryOperationResendRequested({ key })),
      operationDismissed: (key) => dispatch(inventoryOperationDismissed({ key })),
      printReprints: () => {
        const picked = selectInventoryReprints({ resourceId, choice, intent, session })
        if (picked === null || picked.items.length === 0) return
        dispatch(priceTagsPicked({ resourceId, ...picked }))
        openWindow(ONEC_PRICE_TAG_WINDOWS.priceTags, { rid: resourceId })
      },
    }
  }, [choice, dispatch, intent, openWindow, resourceId, session, vm.rows, warehouseRefKey, warehouses.data])

  return <InventoryScreen vm={vm} actions={actions} />
}
